// The marks tiled faintly behind the whole app, in the language the course teaches.
//
// This was six simplified Chinese characters baked into a data URI in body::after, from the years
// when Mandarin was the only course. It did not change when the course did, so a learner of
// Japanese read 単語 and 質問する over 词/说/问, and both English courses showed Chinese to readers
// who had come to learn something else (MYN-161).
//
// Nobody is meant to READ these -- they sit at 2% opacity -- which is the argument for getting
// them right rather than against it: a wallpaper in the wrong language is felt before it is
// noticed, and there is no moment where the learner can point at it and say what is off.
//
// The marks themselves come from the course, in pair.wallpaper. What is decided HERE is only how
// to draw them, which is a rendering question: how big a mark should be set, where it fits inside
// the tile, and which font can draw it.

/** Where the six marks sit in a 420 x 420 tile. Unchanged from the hand-written SVG. */
const SPOTS: readonly (readonly [number, number])[] = [
  [30, 70],
  [250, 120],
  [120, 210],
  [330, 260],
  [60, 330],
  [215, 390],
];

const TILE = 420;

// A data URI is its own document: it cannot see --han or any other custom property, so the stack
// has to be written into the SVG. It is the same answer the stylesheet needs, so it comes from the
// same place -- see faces.ts for why one CJK stack for both languages is not that answer.
import { faceFor } from './faces.js';

/** Latin runs narrow and CJK runs square, and the difference decides whether a mark fits. */
const isLatin = (mark: string): boolean => /^[\x20-\x7e]+$/u.test(mark);

/**
 * How large to set a mark.
 *
 * One character takes the size the tile was designed around. A word cannot: "letter" at 42px is
 * 140px wide and would run off the tile, and a tile that clips its own text repeats that cut every
 * 420px, which reads as damage rather than as decoration.
 */
function sizeOf(mark: string): number {
  const length = [...mark].length;
  if (length === 1) return 42;
  if (length === 2) return 32;
  return 20;
}

/** Enough to keep a mark inside the tile. Deliberately generous rather than exact. */
const widthOf = (mark: string, size: number): number =>
  [...mark].length * size * (isLatin(mark) ? 0.58 : 1);

const escapeXml = (text: string): string =>
  text.replace(/&/gu, '&amp;').replace(/</gu, '&lt;').replace(/>/gu, '&gt;');

/**
 * The wallpaper as a CSS `url(...)`, or `none` for a course that declares no marks.
 *
 * `none` is a real answer, not a failure: a course whose corpus predates MYN-161 has no marks, and
 * no wallpaper is right where a wrong one is not.
 */
export function wallpaperTile(marks: readonly string[] | undefined, code: string): string {
  if (!marks || marks.length === 0) return 'none';
  const text = marks.slice(0, SPOTS.length).map((mark, i) => {
    const [x, y] = SPOTS[i]!;
    const size = sizeOf(mark);
    // Pulled left when it would otherwise cross the tile's edge; never pushed right, because the
    // left edge is where the previous tile's mark already sits.
    const at = Math.max(8, Math.min(x, TILE - widthOf(mark, size) - 8));
    return `<text x='${Math.round(at)}' y='${y}' font-size='${size}'>${escapeXml(mark)}</text>`;
  });
  const svg =
    `<svg xmlns='http://www.w3.org/2000/svg' width='${TILE}' height='${TILE}' viewBox='0 0 ${TILE} ${TILE}'>` +
    `<g fill='#000' font-family="${faceFor(code)}">${text.join('')}</g>` +
    `</svg>`;
  return `url("data:image/svg+xml,${encodeURIComponent(svg)}")`;
}
